import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement} [pagination] - The crawlable pagination container.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** @type {number | null} */
  #visiblePageSyncFrame = null;

  /** @type {boolean} */
  #skipInitialPreviousIntersection = true;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#cacheCurrentPage();
    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    window.addEventListener('popstate', this.#handlePopState);

    if (this.refs.pagination) {
      window.addEventListener('scroll', this.#scheduleVisiblePageSync, { passive: true });
      window.addEventListener('resize', this.#scheduleVisiblePageSync);
      this.#scheduleVisiblePageSync();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    window.removeEventListener('popstate', this.#handlePopState);
    window.removeEventListener('scroll', this.#scheduleVisiblePageSync);
    window.removeEventListener('resize', this.#scheduleVisiblePageSync);

    if (this.#visiblePageSyncFrame !== null) {
      cancelAnimationFrame(this.#visiblePageSyncFrame);
      this.#visiblePageSyncFrame = null;
    }
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                if (this.#skipInitialPreviousIntersection) {
                  this.#skipInitialPreviousIntersection = false;
                  continue;
                }
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return;
    }

    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    history.pushState('', '', nextPage.url.toString());
    this.#updatePaginationForPage(nextPage.page);
    this.#scheduleVisiblePageSync();

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Keep the first existing card at the same viewport position while the
    // previous page is inserted above it.
    const firstElement = grid.firstElementChild;
    const anchorTop = firstElement ? firstElement.getBoundingClientRect().top : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper.processNewElements();

    // Loading content above the viewport must not select that page. The
    // visible-page synchronizer updates the URL and aria-current only when
    // the customer actually scrolls into the newly inserted page.
    const restoreAnchor = () => {
      if (!firstElement) return;

      const heightDiff = firstElement.getBoundingClientRect().top - anchorTop;
      if (Math.abs(heightDiff) < 0.5) return;

      window.scrollTo({
        top: window.scrollY + heightDiff,
        behavior: 'instant',
      });
    };

    restoreAnchor();

    requestAnimationFrame(() => {
      restoreAnchor();
      this.#scheduleVisiblePageSync();
    });

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  /**
   * Synchronize the visible HTML pagination with an automatically loaded page.
   *
   * @param {number} page - The page whose pagination markup should be displayed.
   */
  #updatePaginationForPage(page) {
    const { pagination } = this.refs;
    if (!pagination) return;

    const pageHTML = this.pages.get(page);
    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const nextPagination = parsedPage.querySelector('[ref="pagination"]');
    if (!nextPagination) return;

    const clonedPagination = nextPagination.cloneNode(true);
    pagination.replaceChildren(...clonedPagination.childNodes);
  }

  /**
   * Cache the currently rendered page so browser history can restore its pagination state.
   */
  #cacheCurrentPage() {
    const firstCard = this.querySelector('[ref="cards[]"]');
    const page = Number(firstCard?.getAttribute('data-page'));

    if (!Number.isInteger(page) || page < 1) return;
    this.pages.set(page, this.outerHTML);
  }

  /**
   * Schedule a single visible-page calculation for the next animation frame.
   */
  #scheduleVisiblePageSync = () => {
    if (this.#visiblePageSyncFrame !== null) return;

    this.#visiblePageSyncFrame = requestAnimationFrame(() => {
      this.#visiblePageSyncFrame = null;
      this.#syncUrlWithVisiblePage();
    });
  };

  /**
   * Find the loaded page nearest the vertical center of the viewport.
   *
   * When the center is exactly between two pages, the current URL wins. This
   * prevents rapid URL oscillation at the boundary while the user is stopped.
   *
   * @returns {number | undefined}
   */
  #getVisiblePage() {
    const cards = this.querySelectorAll('[ref="cards[]"][data-page]');
    if (cards.length === 0) return;

    /** @type {Map<number, { first: Element, last: Element }>} */
    const pageGroups = new Map();

    for (const card of cards) {
      const page = Number(card.getAttribute('data-page'));
      if (!Number.isInteger(page) || page < 1) continue;

      const group = pageGroups.get(page);
      if (group) {
        group.last = card;
      } else {
        pageGroups.set(page, { first: card, last: card });
      }
    }

    const viewportCenter = window.innerHeight / 2;
    const currentPage = Number(new URL(window.location.href).searchParams.get('page') || '1');
    let visiblePage;
    let closestDistance = Number.POSITIVE_INFINITY;

    for (const [page, group] of pageGroups) {
      const top = group.first.getBoundingClientRect().top;
      const bottom = group.last.getBoundingClientRect().bottom;
      let distance = 0;

      if (viewportCenter < top) {
        distance = top - viewportCenter;
      } else if (viewportCenter > bottom) {
        distance = viewportCenter - bottom;
      }

      const isCloser = distance < closestDistance;
      const isStableTie = Math.abs(distance - closestDistance) < 1 && page === currentPage;

      if (isCloser || isStableTie) {
        visiblePage = page;
        closestDistance = distance;
      }
    }

    return visiblePage;
  }

  /**
   * Reflect the visible loaded page in the URL without creating a history entry.
   */
  #syncUrlWithVisiblePage() {
    const page = this.#getVisiblePage();
    if (!page) return;

    const url = new URL(window.location.href);
    const currentPage = Number(url.searchParams.get('page') || '1');
    if (page === currentPage) return;

    if (page === 1) {
      url.searchParams.delete('page');
    } else {
      url.searchParams.set('page', page.toString());
    }
    url.hash = '';

    history.replaceState(history.state, '', url.toString());
    this.#updatePaginationForPage(page);
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      this.#cacheCurrentPage();
      this.#scheduleVisiblePageSync();

      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };

  /**
   * Keep the visible pagination synchronized when browser history changes the page URL.
   */
  #handlePopState = () => {
    const page = Number(new URL(window.location.href).searchParams.get('page') || '1');
    this.#updatePaginationForPage(page);

    const firstCardOnPage = this.querySelector(`[ref="cards[]"][data-page="${page}"]`);
    if (firstCardOnPage) {
      firstCardOnPage.scrollIntoView({ behavior: 'instant', block: 'start' });
    }

    this.#scheduleVisiblePageSync();
  };
}
